import React, { useState } from "react";

const FAQS = [
  {
    q: "What types of clients do you work with?",
    a: "We partner with brands, agencies, and in-house marketing teams to produce high-quality visual content for campaigns, advertising, eCommerce, and social platforms. Whether you're launching a new product or refreshing existing assets, the Momenta crew is equipped to support productions of varying scale.",
  },
  {
    q: "Do you work from client briefs?",
    a: "Yes. Many of our productions are guided by established briefs from internal marketing teams or creative agencies. Our role is to execute the vision with precision while ensuring every detail is captured to a commercial standard.",
  },
  {
    q: "What are your payment terms?",
    a: "Projects are confirmed upon receipt of a signed agreement and a 50% deposit, which allows our team to allocate resources and commence pre-production. The remaining balance is invoiced prior to the release of final assets. These terms allow us to commit the necessary planning, crew, and resources to your shoot.",
  },
  {
    q: "What does your production process look like?",
    a: "Our process is designed for clarity, efficiency, and exceptional results at every stage. Following your enquiry, we prepare a tailored proposal outlining scope, deliverables, and production requirements. Once approved, a signed agreement and 50% deposit secure your project in our schedule.\n\nFrom there, we gather detailed information about your brand, products, and shot requirements — whether that includes eCommerce imagery, ad films, campaign assets, web banners, or creative content. We then refine the brief and establish a clear production approach.\n\nPre-production follows, where our team coordinates props, casting, styling, and shoot logistics. Shoot timelines are scheduled based on production needs, with specific dates allocated when talent or on-set attendance is required.\n\nAfter capture, your content moves into post-production. Retouching and editing is completed with precision and typically takes up to two weeks depending on scope. Once final payment has been received, your completed assets are released — ready for launch.",
  },
  {
    q: "Can you assist with models, props, and styling?",
    a: "Absolutely. Through our extended production network, we can coordinate casting, props, set styling, and other shoot elements to ensure a seamless experience.",
  },
  {
    q: "Do you offer ongoing content partnerships?",
    a: "Yes. Many of our clients partner with us on a retainer basis to support consistent, high-quality content production throughout the year. Retainer clients benefit from preferred pricing and priority scheduling, with a six-month minimum commitment in place to ensure continuity and planning across campaigns. Reach out to discuss a structure tailored to your brand's content needs.",
  },
  {
    q: "What is your typical turnaround time?",
    a: "Timelines vary depending on the scale of the production; however, most projects are delivered within 3–4 weeks from start to finish. If you have a specific launch deadline, please let us know during the enquiry stage so we can advise on feasibility.",
  },
];

const Row = ({ q, a, i, isOpen, onToggle }) => (
  <div data-testid={`faq-row-${i}`} className="border-b border-white/10">
    <button
      onClick={onToggle}
      data-testid={`faq-toggle-${i}`}
      className="w-full flex items-start justify-between gap-6 py-6 sm:py-8 text-left group"
      aria-expanded={isOpen}
    >
      <div className="flex items-start gap-5 sm:gap-7 flex-1">
        <h3 className="font-serif text-white text-xl sm:text-2xl lg:text-3xl font-light tracking-tight leading-snug">
          {q}
        </h3>
      </div>
      <span
        className={`text-white text-2xl sm:text-3xl font-light shrink-0 transition-transform duration-300 ${
          isOpen ? "rotate-45" : ""
        }`}
        aria-hidden
      >
        +
      </span>
    </button>
    <div
      data-testid={`faq-answer-${i}`}
      className={`grid transition-all duration-500 ease-out ${
        isOpen ? "grid-rows-[1fr] opacity-100 pb-7 sm:pb-9" : "grid-rows-[0fr] opacity-0"
      }`}
    >
      <div className="overflow-hidden">
        <div className="pr-4 sm:pr-14 text-white/70 text-base sm:text-[17px] font-light leading-relaxed whitespace-pre-line">
          {a}
        </div>
      </div>
    </div>
  </div>
);

export const FAQ = () => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative bg-black border-t border-white/5 py-16 sm:py-24 lg:py-28"
    >
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-10 sm:mb-16">
          <div className="lg:col-span-5">
            <h2 className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[0.95]">
              Good to
              <br />
              <em className="italic font-medium">know</em>.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-white/60 text-base sm:text-lg font-light leading-relaxed">
              Everything you'd ask before sending a brief. Still missing something? <a href="#contact" className="text-white link-underline">Tell us about your project.</a>
            </p>
          </div>
        </div>

        <div className="border-t border-white/10">
          {FAQS.map((f, i) => (
            <Row
              key={f.q}
              i={i}
              q={f.q}
              a={f.a}
              isOpen={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
